// =============================================================================
// FOOTER MASCOT — Horizon `Component` port of the footer Brewster mascot reveal. This reparents the
// EXACT behaviour of the bundled src/js/objects/global/footer-mascot-reveal.js onto Horizon's
// `Component` base class, loaded as native ESM via the `@theme/*` importmap
// (snippets/horizon-importmap.liquid) rather than the webpack src/js bundle.
//
// The mascot is a per-breakpoint, PLAY-ONCE three-frame animation (start → mid → end), driven by
// merchant image_picker settings. The crossfade itself is pure CSS (@keyframes, iteration-count: 1,
// fill: forwards) in src/scss/objects/global/_footer.scss, keyed on `.ub-footer__mascot.is-revealed`
// and the per-group `--count-{1,2,3}` modifier. Because the footer sits below the fold, a plain
// load-time CSS animation would finish unseen — so this element only TRIGGERS the animation when it
// scrolls into view, by adding `.is-revealed` once.
//
// This element IS the mascot (`<footer-mascot class="ub-footer__mascot">`, sections/footer.liquid), so
// it toggles the state-classes on ITSELF and needs no refs — the frames are CSS-addressed descendants
// and their `animationend` BUBBLES up to this element.
//
//   • One-shot: the observer unobserves after the first intersection, so it never loops/replays.
//   • HOVER hand-off: once the intro settles, the `fill: forwards` @keyframes LOCK each frame's
//     opacity, which would override a plain CSS :hover. So on the intro's first `animationend` this
//     element REMOVES `.is-revealed` (un-matching the intro animation rules — the (0,4,0) intro
//     selectors out-specify `.intro-done`'s defensive `animation: none` (0,3,0), so removing the class
//     is the clean release) and adds `.intro-done`, remove-then-add in ONE synchronous handler. The
//     `.intro-done` rest state replicates the fill-forwards settled state, so the swap paints nothing.
//     REPLAY HAZARD: `.is-revealed` must never be re-added while `.intro-done` is present — both
//     classes together REPLAY the intro (specificity fact above). Hence the #armed guard below.
//   • The listener is `{ once: true }`: all intro frames settle together, so the first `animationend`
//     means "intro done" (count-3 fires 3, count-2 fires 2 — the rest are ignored). The hand-off is
//     ATTACHED BEFORE any reveal, so an above-the-fold mascot can't finish its intro unheard. The
//     hover crossfade itself is a CSS transition (fires `transitionend`, never `animationend`), so it
//     can't re-trigger the hand-off. A static frame (count-1) or reduced-motion fires NO
//     `animationend`, so `.intro-done` is never added and hover stays disabled — both intended.
//   • prefers-reduced-motion: PURE CSS gate — deliberately NO prefersReducedMotion() JS gate here
//     (unlike the media-with-content-note reference). The reduce @media in _footer.scss shows the
//     settled frame statically and disables the keyframes; adding `.is-revealed` is harmless and the
//     reduce block relies on that shape.
//   • No IntersectionObserver: `.is-revealed` is added immediately (animation plays on next paint,
//     likely unseen below the fold — accepted); the hand-off is already armed, so hover still works.
//   • No frames set: Liquid omits the element entirely — nothing runs.
//
// EDITOR DELTA (accepted improvement): the old bundled module never re-armed on
// shopify:section:load, so an editor re-render left the fresh mascot inert until full reload. A fresh
// editor-rendered node upgrades + arms naturally via connectedCallback. The storefront path is
// identical. A morph RE-CONNECT of the SAME node is blocked by #armed (see replay hazard above).
//
// `type="module"` scripts DEFER and run AFTER Motto's classic head scripts. This behaviour is fully
// self-contained (own subtree + IntersectionObserver only, no Motto load-order global), so the defer
// is safe. Vendored Motto assets/footer.js (the accordion toggle) is untouched — this is additive.
//
// UNTRACKED new file (alongside assets/component.js + assets/utilities.js) — re-verify it exists
// before any push; a parallel agent's git op on assets/ can remove it.
// =============================================================================
import { Component } from '@theme/component';

// State classes — _footer.scss keys the intro animation on REVEALED and the rest state + :hover
// crossfade on DONE. Names kept as-is (parity with the bundled module; the `is-revealed` name-share
// with featured-reviews is inert — its rules are compounded on `.review-card`, ours on
// `.ub-footer__mascot`).
const REVEALED = 'is-revealed';
const DONE = 'intro-done';

/**
 * The footer Brewster mascot custom element. No refs — it operates on itself (classList +
 * `animationend` bubbling from the frame descendants).
 *
 * @extends {Component}
 */
export class FooterMascot extends Component {
  // Guard against a double connectedCallback (e.g. a morph re-connect without a fresh node) double-
  // attaching the once-listener or RE-observing a node that already carries `.intro-done` — which
  // would re-add `.is-revealed` and replay the intro (see replay hazard above). The old bundled
  // module ran exactly once via webpack module caching; this field is the per-instance equivalent.
  #armed = false;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback(); // populates this.refs + wires Component events
    if (this.#armed) return;
    this.#armed = true;

    // Attach the intro→hover hand-off BEFORE revealing, so an above-the-fold mascot can't finish its
    // intro before we are listening. `animationend` bubbles from the animated frames up to this.
    this.addEventListener('animationend', () => this.#handOffToHover(), {
      once: true,
    });

    // Fallback: no IntersectionObserver → just reveal (animation plays on next paint).
    if (typeof IntersectionObserver === 'undefined') {
      this.classList.add(REVEALED);
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(REVEALED);
            obs.unobserve(entry.target); // one-shot — never replays
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.35 }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback(); // Component's own mutation-observer cleanup
    this.#observer?.disconnect(); // per-instance IO cleanup (old module leaked it page-lifetime)
  }

  // The intro→hover hand-off: remove-then-add in one synchronous handler (one style recalc) so the
  // base `:first-child { opacity: 1 }` rule never paints between the two states (flicker fix — the
  // `.intro-done` rest state replicates the fill-forwards settled state exactly).
  #handOffToHover() {
    this.classList.remove(REVEALED); // stop the intro animation rules from matching (release the lock)
    this.classList.add(DONE); // switch to resting + :hover crossfade (see _footer.scss)
  }
}

if (!customElements.get('footer-mascot')) {
  customElements.define('footer-mascot', FooterMascot);
}
